import React, { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { useParams } from "react-router-dom";
import {
  GoogleMap,
  DirectionsRenderer,
  DirectionsService,
  Marker,
} from "@react-google-maps/api";
import { styled } from '@mui/material/styles';
import {
  Card,
  CardActions,
  CardContent,
  Button,
  Typography,
  Rating,
  Badge,
  Box,
  Grid,
} from "@mui/material";
import {
  Person as PersonIcon,
  LocalPhone as LocalPhoneIcon,
  AccessTime as AccessTimeIcon,
  LocationOnRounded as LocationOnRoundedIcon,
  MonetizationOnRounded as MonetizationOnRoundedIcon,
  RoundaboutLeft as RoundaboutLeftIcon,
  Autorenew as AutorenewIcon,
  DirectionsCar as CarIcon,
  LocalHospital as LocalHospitalIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AirlineSeatFlat as PatientIcon,
  DriveEta as DriveEtaIcon,
} from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchDriverImage, fetchPassengerImage } from "../fetchDocs";
import Spinner from "../Spinner";
 // Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  boxShadow: theme.shadows[4],
  borderRadius: "12px",
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  margin: '0 0 20px 0',
  border: '1px solid #e0e0e0',
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));
const ImageContainer = styled('div')({
  height: 200,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #e0e0e0',
});

const InfoItem = styled('div')(({ theme }) => ({
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1),
  borderRadius: '6px',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const MapContainer = styled('div')(({ theme }) => ({
  height: '550px',
  width: '100%',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: theme.shadows[4],
  border: `2px solid ${theme.palette.divider}`,
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: '0.75rem',
    fontWeight: 600,
    padding: '4px 12px',
    height: 26,
    minWidth: 70,
    borderRadius: 13,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: '1.1rem',
}));

const CardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderBottom: `2px solid ${theme.palette.divider}`,
  backgroundColor: '#fafafa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const EmergencyBanner = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff3e0',
  border: '2px solid #ff9800',
  borderRadius: '8px',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

// Map configuration
const defaultMapOptions = {
  fullscreenControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  zoomControl: true,
  styles: [
    {
      featureType: "all",
      elementType: "geometry.fill",
      stylers: [{ weight: "2.00" }]
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#e9e9e9" }]
    },
    {
      featureType: "landscape",
      elementType: "geometry",
      stylers: [{ color: "#f5f5f5" }]
    },
    {
      featureType: "road",
      elementType: "geometry",
      stylers: [{ color: "#ffffff" }]
    }
  ]
};

const ViewReservation = () => {
  const [passengerId, setPassengerId] = useState("");
  const [pickUpLocationLatitude, setPickUpLocationLatitude] = useState("");
  const [pickUpLocationLongitude, setPickUpLocationLongitude] = useState("");
  const [destinationLocationLatitude, setDestinationLocationLatitude] = useState("");
  const [destinationLocationLongitude, setDestinationLocationLongitude] = useState("");
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetched, setFetched] = useState(false);
  const [response, setResponse] = useState(null);
  const [travelMode, setTravelMode] = useState("DRIVING");
  const [pickupTime, setPickupTime] = useState(null);
  const [dropOffTime, setDropOffTime] = useState(null);
  const [totalDistance, setTotalDistance] = useState(0);
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [driver, setDriver] = useState(null);
  const [passenger, setPassenger] = useState(null);
  const [status, setStatus] = useState();
  const [driverImage, setDriverImage] = useState();
  const [passengerImage, setPassengerImage] = useState();
  const [tripLocations, setTripLocations] = useState([]);
  const [tripRequest, setTripRequest] = useState(null);
  const [finalPrice, setFinalPrice] = useState(0);
  const [approachFee, setApproachFee] = useState(0);
  const [tripDuration, setTripDuration] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [acceptanceLocation, setAcceptanceLocation] = useState(null);
  const [pickUpPlace, setPickUpPlace] = useState("");
  const [destinationPlace, setDestinationPlace] = useState("");
  const [actualPrice, setActualPrice] = useState(0);
  const [actualDistance, setActualDistance] = useState(0);
  const [vehicleType, setVehicleType] = useState("");
  const [crewSize, setCrewSize] = useState(null);
  const [isEmergency, setIsEmergency] = useState(false);
  const [emergencyType, setEmergencyType] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [hospitalId, setHospitalId] = useState(null);

  let id = useParams().id;

  const notifyError = () => toast.error("Something went wrong ...", {
    position: "top-right",
    autoClose: 1000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });

  const fetchData = async () => {
    try {
      console.log("TripDetail component loaded - fetching trip data...");
      const res = await axios.get("/tripDetails/" + id);
      console.log("Trip data received:", res.data);
      const trLocations = await axios.get("/tripLocations/" + id);
      setTripLocations(trLocations.data);

      setPassengerId(res.data.passengerId);
      setPickUpLocationLatitude(res.data.pickUpLocationLatitude);
      setPickUpLocationLongitude(res.data.pickUpLocationLongitude);
      setDestinationLocationLatitude(res.data.destinationLocationLatitude);
      setDestinationLocationLongitude(res.data.destinationLocationLongitude);
      setOrigin(res.data.pickUpLocationLatitude + "," + res.data.pickUpLocationLongitude);
      setDestination(res.data.destinationLocationLatitude + "," + res.data.destinationLocationLongitude);
      setPickupTime(res.data.pickUpTime);
      setDropOffTime(res.data.dropOffTime);
      setTotalDistance(res.data.totalDistance);
      setEstimatedPrice(res.data.estimatedPrice);
      setStatus(res.data.status);
      setDriver(res.data.driver);
      setPassenger(res.data.passenger);
      setTripDuration(res.data.tripDuration);
      setPickUpPlace(res.data.pickUpPlace || "");
      setDestinationPlace(res.data.destinationPlace || "");
      setActualPrice(res.data.actualPrice || 0);
      setActualDistance(res.data.actualTotalDistance || 0);
      setVehicleType(res.data.vehicleType || "");
      setCrewSize(res.data.crewSize);
      setIsEmergency(res.data.isEmergency || false);
      setEmergencyType(res.data.emergencyType);
      setSeverity(res.data.severity);
      setHospitalId(res.data.hospitalId);

      // Set TripRequest related data
      if (res.data.TripRequest) {
        setTripRequest(res.data.TripRequest);
        setFinalPrice(res.data.TripRequest.finalPrice || 0);
        setApproachFee(res.data.TripRequest.approachFee || 0);
        setPaymentMethod(res.data.TripRequest.paymentMethod);
        if (res.data.TripRequest.acceptanceLocationLatitude && res.data.TripRequest.acceptanceLocationLongitude) {
          setAcceptanceLocation({
            latitude: res.data.TripRequest.acceptanceLocationLatitude,
            longitude: res.data.TripRequest.acceptanceLocationLongitude
          });
        }
      }

      if (res.data.passenger && res.data.passenger.passengerImage) {
        fetchPassengerImage(res.data.passenger.passengerImage, setPassengerImage);
      }

      if (res.data.driver && res.data.driver.driverImage) {
        fetchDriverImage(res.data.driver.driverImage, setDriverImage);
      }

      setLoading(false);
    } catch (err) {
      notifyError();
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const directionsCallback = (response) => {
    if (!fetched && response !== null && response.status === "OK") {
      setFetched(true);
      setResponse(response);
    }
  };

  const renderPassengerCard = () => (
    <StyledCard>
      <CardHeader>
        <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PatientIcon color="primary" />
          Patient Information
        </Typography>
      </CardHeader>
      <ImageContainer>
        {passengerImage ? (
          <img
            src={passengerImage}
            alt="Patient"
            style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "cover", borderRadius: '8px'}}
          />
        ) : (
          <PersonIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />
        )}
      </ImageContainer>
      <CardContent sx={{ pt: 2.5 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {passenger?.firstName} {passenger?.lastName}
        </Typography>
        {passenger?.rating > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Rating value={passenger?.rating} readOnly size="small" />
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({typeof passenger?.rating === 'number' ? passenger.rating.toFixed(1) : passenger?.rating})
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <LocalPhoneIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {passenger?.phoneNumber}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );

  const renderDriverCard = () => (
    <StyledCard>
      <CardHeader>
        <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DriveEtaIcon color="primary" />
          Driver Information
        </Typography>
      </CardHeader>
      <ImageContainer>
        {driverImage ? (
          <img
            src={driverImage}
            alt="Driver"
            style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "cover", borderRadius: '8px' }}
          />
        ) : (
          <PersonIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />
        )}
      </ImageContainer>
      <CardContent sx={{ pt: 2.5 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          {driver?.firstName} {driver?.lastName}
        </Typography>
        {driver?.rating > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Rating value={driver?.rating} readOnly size="small" />
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              ({typeof driver?.rating === 'number' ? driver.rating.toFixed(1) : driver?.rating})
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <LocalPhoneIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {driver?.phoneNumber}
          </Typography>
        </Box>
      </CardContent>
    </StyledCard>
  );

  const renderTripInfoCard = () => (
    <StyledCard>
      <CardHeader>
        <Typography variant="subtitle1" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospitalIcon color="primary" />
          Trip Details
        </Typography>
        <StyledBadge
          badgeContent={status}
          color={status === "Active" ? "warning" : "success"}
        />
      </CardHeader>
      <CardContent sx={{ pt: 2 }}>
        {isEmergency && (
          <EmergencyBanner>
            <WarningIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#e65100' }}>
                Emergency Transport
              </Typography>
              {emergencyType && (
                <Typography variant="caption" sx={{ color: '#ef6c00' }}>
                  Type: {emergencyType}
                </Typography>
              )}
            </Box>
          </EmergencyBanner>
        )}

        <InfoItem>
          <LocationOnRoundedIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Pickup Location
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {pickUpPlace}
            </Typography>
          </Box>
        </InfoItem>

        <InfoItem>
          <ScheduleIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Pickup Time
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {pickupTime && format(new Date(pickupTime), "p, dd/MM/yyyy")}
            </Typography>
          </Box>
        </InfoItem>

        {tripDuration && (
          <InfoItem>
            <AccessTimeIcon color="primary" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Duration
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {tripDuration}
              </Typography>
            </Box>
          </InfoItem>
        )}

        <InfoItem>
          <LocationOnRoundedIcon color="error" fontSize="small" />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Destination
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {destinationPlace}
            </Typography>
          </Box>
        </InfoItem>

        {vehicleType && (
          <InfoItem>
            <CarIcon color="primary" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Vehicle Type
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {vehicleType}
              </Typography>
            </Box>
          </InfoItem>
        )}

        {crewSize && (
          <InfoItem>
            <LocalHospitalIcon color="primary" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Medical Crew
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {crewSize} personnel
              </Typography>
            </Box>
          </InfoItem>
        )}

        <InfoItem>
          <RoundaboutLeftIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              {actualDistance > 0 ? 'Estimated / Actual Distance' : 'Estimated Distance'}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {totalDistance} KM
              {actualDistance > 0 && ` / ${parseFloat(actualDistance).toFixed(2)} KM`}
            </Typography>
          </Box>
        </InfoItem>

        <InfoItem>
          <MonetizationOnRoundedIcon color="primary" fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Pricing
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              Est: {parseFloat(estimatedPrice).toFixed(2)} ETB
            </Typography>
            {actualPrice && actualPrice !== "NaN" && actualPrice !== "0" && (
              <Typography variant="body2" fontWeight={500} color="success.main">
                Actual: {typeof actualPrice === 'string' ? parseFloat(actualPrice).toFixed(2) : actualPrice.toFixed(2)} ETB
              </Typography>
            )}
            {finalPrice > 0 && (
              <Typography variant="body2" fontWeight={600} color="primary.main">
                Final: {parseFloat(finalPrice).toFixed(2)} ETB
              </Typography>
            )}
            {approachFee > 0 && (
              <Typography variant="caption" color="text.secondary">
                Approach Fee: {parseFloat(approachFee).toFixed(2)} ETB
              </Typography>
            )}
          </Box>
        </InfoItem>

        {paymentMethod && (
          <InfoItem>
            <CheckCircleIcon color="success" fontSize="small" />
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Payment Method
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {paymentMethod}
              </Typography>
            </Box>
          </InfoItem>
        )}
      </CardContent>
    </StyledCard>
  );

  const renderMap = () => (
    <Box>
      <MapContainer>
        <GoogleMap
          id="direction-example"
          mapContainerStyle={{
            height: "100%",
            width: "100%",
          }}
          zoom={8}
          center={{
            lat: parseFloat(pickUpLocationLatitude) || 9.005401,
            lng: parseFloat(pickUpLocationLongitude) || 38.763611,
          }}
          options={defaultMapOptions}
        >
          <DirectionsService
            options={{
              destination: `${destinationLocationLatitude},${destinationLocationLongitude}`,
              origin: `${pickUpLocationLatitude},${pickUpLocationLongitude}`,
              travelMode: travelMode,
            }}
            callback={directionsCallback}
          />
          {response !== null && (
            <DirectionsRenderer
              options={{
                directions: response,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: "#2196F3",
                  strokeWeight: 5,
                  strokeOpacity: 0.8,
                },
              }}
            />
          )}
          <Marker
            position={{
              lat: parseFloat(pickUpLocationLatitude),
              lng: parseFloat(pickUpLocationLongitude),
            }}
          />
          <Marker
            position={{
              lat: parseFloat(destinationLocationLatitude),
              lng: parseFloat(destinationLocationLongitude),
            }}
          />
        </GoogleMap>
      </MapContainer>
    </Box>
  );

  if (loading) return <Spinner />;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, backgroundColor: '#fafafa', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
        Trip Details
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Passenger Card */}
        <Grid item xs={12} md={4}>
          {renderPassengerCard()}
        </Grid>
        {/* Driver Card */}
        <Grid item xs={12} md={4}>
          {renderDriverCard()}
        </Grid>
        {/* Trip Info Card */}
        <Grid item xs={12} md={4}>
          {renderTripInfoCard()}
        </Grid>
      </Grid>

      {/* Map */}
      <Box sx={{ position: "relative", mt: 4 }}>
        <SectionTitle>
          <RoundaboutLeftIcon color="primary" />
          Trip Route
        </SectionTitle>
        {renderMap()}
      </Box>

      {/* Toast Notifications */}
      <ToastContainer />
    </Box>
  );
};

export default ViewReservation;